<script setup lang="ts">
import { pToggleVariants } from '@ui/components/core/p-toggle/index.js';
import type { ToggleGroupItemProps } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import type { PToggleGroupVariants } from './context.js';
import { reactiveOmit } from '@vueuse/core';
import { ToggleGroupItem, useForwardProps } from 'reka-ui';
import { computed, inject } from 'vue';
import { cn } from '@ui/lib/utils.js';
import { pToggleGroupContextKey } from './context.js';

const props = defineProps<
    ToggleGroupItemProps & {
        class?: HTMLAttributes['class'];
        variant?: PToggleGroupVariants['variant'];
        size?: PToggleGroupVariants['size'];
        rounded?: boolean;
    }
>();

const context = inject(pToggleGroupContextKey);

const resolvedVariant = computed(() => context?.variant.value ?? props.variant);
const resolvedSize = computed(() => context?.size.value ?? props.size);
const resolvedSpacing = computed(() => context?.spacing.value);
const resolvedRounded = computed(() => context?.rounded.value ?? props.rounded);

const delegatedProps = reactiveOmit(props, 'class', 'size', 'variant', 'rounded');
const forwardedProps = useForwardProps(delegatedProps);
</script>

<template>
    <ToggleGroupItem
        v-slot="slotProps"
        data-slot="toggle-group-item"
        :data-variant="resolvedVariant"
        :data-size="resolvedSize"
        :data-spacing="resolvedSpacing"
        :data-rounded="resolvedRounded"
        v-bind="forwardedProps"
        :class="
            cn(
                pToggleVariants({
                    variant: resolvedVariant,
                    size: resolvedSize,
                }),
                'w-auto min-w-0 shrink-0 px-3 focus:z-10 focus-visible:z-10',
                resolvedRounded
                    ? 'rounded-full data-[spacing=0]:rounded-none data-[spacing=0]:first:rounded-l-full data-[spacing=0]:last:rounded-r-full'
                    : 'data-[spacing=0]:rounded-none data-[spacing=0]:first:rounded-l-lg data-[spacing=0]:last:rounded-r-lg',
                'data-[spacing=0]:shadow-none',
                'data-[spacing=0]:data-[variant=outline]:border-l-0 data-[spacing=0]:data-[variant=outline]:first:border-l',
                props.class,
            )
        "
    >
        <slot v-bind="slotProps" />
    </ToggleGroupItem>
</template>
